import { useEffect, useRef, useState } from "react";
import { Download, X, Share, Plus, SquarePlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";

/**
 * InstallAppPrompt
 *  • Captures the browser's beforeinstallprompt event (Chromium / Edge / Samsung Internet)
 *  • Renders a small banner near the bottom of the page after a short delay
 *  • Hidden once the user installs, dismisses, or when already running as PWA
 *  • iOS: shows a banner whose button opens a step-by-step Add-to-Home-Screen guide
 */
export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    // Already installed / running standalone?
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Was dismissed permanently in this session/local?
    if (localStorage.getItem("ddf_pwa_dismissed") === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // brief delay so it doesn't fight with the subscribe modal
      setTimeout(() => setVisible(true), 1500);
      if (!firedRef.current) {
        firedRef.current = true;
        trackEvent("pwa_prompt_available", {});
      }
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      trackEvent("pwa_installed", {});
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari fallback: no beforeinstallprompt; show a banner that opens a guide.
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIOS && !window.navigator.standalone) {
      setTimeout(() => setIosHint(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    trackEvent("pwa_install_click", {});
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    trackEvent("pwa_install_outcome", { outcome });
    setVisible(false);
    setDeferred(null);
  };

  const openIosGuide = () => {
    trackEvent("pwa_ios_guide_open", {});
    setIosGuideOpen(true);
  };

  const dismiss = () => {
    localStorage.setItem("ddf_pwa_dismissed", "1");
    setVisible(false);
    setIosHint(false);
    trackEvent("pwa_prompt_dismissed", {});
  };

  if (!visible && !iosHint) return null;

  return (
    <>
      <div className="ddf-pwa-banner" role="dialog" aria-label="Install App" data-testid="pwa-install-banner">
        <div className="ddf-pwa-banner-inner">
          <img src="/eflyericon.png" alt="" className="ddf-pwa-icon" />
          <div className="ddf-pwa-text">
            <p className="ddf-pwa-title">Install DDF eFlyer</p>
            {visible ? (
              <p className="ddf-pwa-sub">Get the app experience — faster loads, full-screen.</p>
            ) : (
              <p className="ddf-pwa-sub">Add DDF eFlyer to your Home Screen.</p>
            )}
          </div>
          {visible && (
            <button
              type="button"
              className="ddf-pwa-install-btn"
              onClick={install}
              data-testid="pwa-install-btn"
            >
              <Download size={14} /> Install
            </button>
          )}
          {!visible && iosHint && (
            <button
              type="button"
              className="ddf-pwa-install-btn"
              onClick={openIosGuide}
              data-testid="pwa-ios-install-btn"
            >
              <Download size={14} /> Install
            </button>
          )}
          <button
            type="button"
            className="ddf-pwa-close-btn"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            data-testid="pwa-install-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* iOS Add-to-Home-Screen step-by-step guide */}
      <Dialog open={iosGuideOpen} onOpenChange={setIosGuideOpen}>
        <DialogContent className="ddf-ios-guide" data-testid="pwa-ios-guide-dialog">
          <DialogHeader>
            <DialogTitle>Install DDF eFlyer</DialogTitle>
            <DialogDescription>
              Add this app to your iPhone or iPad Home Screen in 3 quick steps.
            </DialogDescription>
          </DialogHeader>
          <ol className="ddf-ios-steps">
            <li className="ddf-ios-step">
              <span className="ddf-ios-step-num">1</span>
              <span className="ddf-ios-step-body">
                Tap the <b>Share</b> button
                <Share size={18} className="ddf-ios-step-ic" />
                in the Safari toolbar.
              </span>
            </li>
            <li className="ddf-ios-step">
              <span className="ddf-ios-step-num">2</span>
              <span className="ddf-ios-step-body">
                Scroll down and tap <b>Add to Home Screen</b>
                <SquarePlus size={18} className="ddf-ios-step-ic" />.
              </span>
            </li>
            <li className="ddf-ios-step">
              <span className="ddf-ios-step-num">3</span>
              <span className="ddf-ios-step-body">
                Tap <b>Add</b>
                <Plus size={18} className="ddf-ios-step-ic" />
                in the top-right corner. Done!
              </span>
            </li>
          </ol>
          <p className="ddf-ios-note">
            The DDF eFlyer icon will appear on your Home Screen for one-tap access.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
